// src/components/staff/StaffDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Users, Car, Clock, CheckCircle, Bell, Calendar, Phone, AlertTriangle } from 'lucide-react';
import { useStudentContext } from '../../contexts/StudentContext';
import { shouldShowStudentToday } from '../../utils/dateUtils';
import { styles } from '../../styles/theme';
import { StatsCard } from '../shared/Card';
import { getTodaysSessions, getStageInfo, calculateLateThresholds } from '../../services/driverSessionService';
import { getAllDrivers as fetchAllDrivers } from '../../services/driverService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const StaffDashboard = ({ onNavigate, onSelectDriver, showBackButton, onBack }) => {
  const { allStudents, getStudentsByDriverForDay, getPickupStudents } = useStudentContext();
  const [driverSessions, setDriverSessions] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [vacationModal, setVacationModal] = useState(null); // { studentId, studentName }
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');
  const [savingAbsent, setSavingAbsent] = useState(null);

  const handleMarkAbsent = async (studentId) => {
    setSavingAbsent(studentId);
    try {
      await updateDoc(doc(db, 'students', studentId), {
        attendanceStatus: 'absent',
        absentDate: new Date().toISOString(),
      });
    } catch (err) {
      alert('Failed to mark absent.');
    } finally {
      setSavingAbsent(null);
    }
  };

  const handleMarkPresent = async (studentId) => {
    setSavingAbsent(studentId);
    try {
      await updateDoc(doc(db, 'students', studentId), {
        attendanceStatus: 'present',
        absentDate: null,
      });
    } catch (err) {
      alert('Failed to update.');
    } finally {
      setSavingAbsent(null);
    }
  };

  const handleSaveVacation = async () => {
    if (!vacationStart || !vacationEnd) { alert('Please enter both start and end dates.'); return; }
    setSavingAbsent(vacationModal.studentId);
    try {
      await updateDoc(doc(db, 'students', vacationModal.studentId), {
        onBreak: true,
        breakStartDate: vacationStart,
        breakEndDate: vacationEnd,
        breakReason: 'Vacation',
      });
      setVacationModal(null);
      setVacationStart('');
      setVacationEnd('');
    } catch (err) {
      alert('Failed to save vacation.');
    } finally {
      setSavingAbsent(null);
    }
  };

  // Load driver sessions and driver list, refresh every 60 seconds
  useEffect(() => {
    const load = async () => {
      try {
        const [sessions, drivers] = await Promise.all([
          getTodaysSessions(),
          fetchAllDrivers(),
        ]);
        setDriverSessions(sessions);
        setAllDrivers(drivers);
        setLastRefresh(new Date());
      } catch (err) {
        console.error('Error loading driver data:', err);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  // Get today's day name
const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

// Helper function to check if student is scheduled today
const isScheduledToday = (student) => {
  // Check if program has started
  if (student.startDate && !shouldShowStudentToday(student.startDate)) {
    return false;
  }
  // Filter out absent students
  if (student.attendanceStatus === 'absent') return false;

  const pickupDays = student.pickupDays || '';
  if (typeof pickupDays === 'string') {
    return pickupDays.toLowerCase().includes(today.toLowerCase());
  } else if (Array.isArray(pickupDays)) {
    return pickupDays.some(day => day.toLowerCase() === today.toLowerCase());
  }
  return false;
};

// All pickup students actually scheduled today (already active + needsPickup, from context)
const todaysPickupStudents = getPickupStudents().filter(isScheduledToday);

// Build each driver's route dynamically (works for any number of real drivers,
// and respects per-day driverSchedule overrides)
const studentsByDriverId = {};
allDrivers.forEach(driver => {
  studentsByDriverId[driver.id] = getStudentsByDriverForDay(driver.id, today).filter(isScheduledToday);
});

// Calculate totals from today's actual pickup students
const totalPickupStudents = todaysPickupStudents.length;
const totalInTransit = todaysPickupStudents.filter(s => s.status === 'picked-up').length;
const totalDelivered = todaysPickupStudents.filter(s => s.status === 'delivered').length;
const totalPending = todaysPickupStudents.filter(s => s.status === 'pending').length;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.centerContainer}>

          {showBackButton && (
  <button 
    style={{...styles.backButton, backgroundColor: '#3b82f6', marginRight: '0.5rem'}}
    onClick={onBack}
  >
    ← Back to Admin
  </button>
)}
          <button 
            style={{...styles.backButton, backgroundColor: '#dc2626'}}
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              setTimeout(() => {
                window.location.href = '/';
              }, 100);
            }}
          >
            🚪 Logout
          </button>
          <div style={styles.flexBetween}>
            <div>
              <h1 style={styles.headerTitle}>Staff Dashboard</h1>
              <p style={styles.headerSubtitle}>Real-time monitoring and management</p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                style={{...styles.button, backgroundColor: '#10b981'}}
                onClick={() => onNavigate('todays-students')}
                title="View all students scheduled for today"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar style={{ width: '16px', height: '16px' }} />
                  <span>Today's Roster</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.centerContainer}>
        {/* ── Pickup Monitor ── */}
        <div style={{ marginBottom: '1.5rem', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>🚐 Pickup Monitor</div>
            <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Refreshes every 60s · Last: {lastRefresh.toLocaleTimeString()}</div>
          </div>

          {allDrivers.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>No active drivers found.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1px', backgroundColor: '#e5e7eb' }}>
              {allDrivers.filter(d => d.status === 'active' || d.status === 'backup').map(driver => {
                const session = driverSessions.find(s => s.driverId === driver.id);
                const stage = getStageInfo(session?.journeyStatus || 'idle');
                const pickedUp = session?.studentsPickedUp || 0;
                const delivered = session?.studentsDelivered || 0;
                const total = session?.studentsTotal || 0;
                const pending = Math.max(0, total - pickedUp);

                // Alert if no update in 20+ minutes and journey is active
                const lastUpdated = session?.lastUpdatedAt?.toDate?.() || null;
                const minutesSince = lastUpdated ? Math.floor((Date.now() - lastUpdated.getTime()) / 60000) : null;
                const isStale = minutesSince !== null && minutesSince > 20 && session?.journeyStatus !== 'idle' && session?.journeyStatus !== 'arrived';

                return (
                  <div key={driver.id} style={{ backgroundColor: 'white', padding: '1.25rem' }}>
                    {/* Driver header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937' }}>{driver.name}</div>
                        <a href={`tel:${driver.phone}`} style={{ fontSize: '0.8rem', color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.15rem' }}>
                          <Phone size={12} /> {driver.phone}
                        </a>
                      </div>
                      {isStale && (
                        <span style={{ fontSize: '0.68rem', backgroundColor: '#fef2f2', color: '#dc2626', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: '700', border: '1px solid #fecaca' }}>
                          ⚠️ No update {minutesSince}m
                        </span>
                      )}
                    </div>

                    {/* Journey stage */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.3rem 0.75rem', borderRadius: '20px', backgroundColor: stage.color + '18', border: `1.5px solid ${stage.color}40`, marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.9rem' }}>{stage.icon}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: '700', color: stage.color }}>{stage.label}</span>
                    </div>
                    {lastUpdated && session?.journeyStatus !== 'idle' && (
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
                        Updated {minutesSince === 0 ? 'just now' : `${minutesSince}m ago`}
                      </div>
                    )}

                    {/* Student counts */}
                    {total > 0 && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {[
                          { label: 'Pending', value: pending, color: '#f59e0b', bg: '#fffbeb' },
                          { label: 'In Transit', value: pickedUp - delivered, color: '#3b82f6', bg: '#eff6ff' },
                          { label: 'Delivered', value: delivered, color: '#10b981', bg: '#f0fdf4' },
                        ].map(item => (
                          <div key={item.label} style={{ flex: 1, textAlign: 'center', padding: '0.4rem', backgroundColor: item.bg, borderRadius: '6px' }}>
                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: item.color }}>{item.value}</div>
                            <div style={{ fontSize: '0.6rem', color: '#6b7280' }}>{item.label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {total === 0 && session?.journeyStatus === 'idle' && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>Not started today</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* ── Late Alert Banner ── */}
        {(() => {
          const lateAlerts = allDrivers
            .filter(d => d.status === 'active' || d.status === 'backup')
            .flatMap(driver => {
              const session = driverSessions.find(s => s.driverId === driver.id);
              if (session?.journeyStatus !== 'idle') return []; // already started
              const driverStudents = studentsByDriverId[driver.id] || [];
              if (driverStudents.length === 0) return [];
              const thresholds = calculateLateThresholds(driver, driverStudents);
              return thresholds
                .filter(t => t.isLateFromHome || t.isLateFromCenter)
                .map(t => ({ driver, threshold: t }));
            });

          if (lateAlerts.length === 0) return null;
          return (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fef2f2', border: '2px solid #fecaca', borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <AlertTriangle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '0.1rem' }} />
              <div>
                <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                  Driver Late — Plan Backup
                </div>
                {lateAlerts.map((alert, i) => (
                  <div key={i} style={{ fontSize: '0.8rem', color: '#991b1b', marginBottom: '0.2rem' }}>
                    <strong>{alert.driver.name}</strong> should have left for <strong>{alert.threshold.schoolName}</strong> by {alert.threshold.mustLeaveCenter || alert.threshold.mustLeaveHome} — dismissal at {alert.threshold.dismissalTime}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Pickup Student Table ── */}
        {(() => {
          const pickupStudents = [...todaysPickupStudents].sort((a, b) => `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`));

          if (pickupStudents.length === 0) return null;

          return (
            <div style={{ marginBottom: '1.5rem', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #e5e7eb', fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>
                👦 Today's Pickup Students ({pickupStudents.length})
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Student', 'School', 'Dismissal', 'Driver', 'Status', 'Absent / Vacation'].map(h => (
                        <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: '700', color: '#6b7280', fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pickupStudents.map((s, idx) => {
                      const isWed = new Date().getDay() === 3;
                      const dismissal = isWed ? s.dismissalTimeWednesday : s.dismissalTimeNormal;
                      const driver = allDrivers.find(d => d.id === s.assignedDriver);
                      const isAbsent = s.attendanceStatus === 'absent';
                      const onVacation = s.onBreak && s.breakReason === 'Vacation';
                      const statusColor = s.status === 'delivered' ? '#10b981' : s.status === 'picked-up' ? '#3b82f6' : '#f59e0b';
                      const statusLabel = s.status === 'delivered' ? 'Delivered' : s.status === 'picked-up' ? 'In Transit' : 'Pending';

                      return (
                        <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: isAbsent || onVacation ? '#fef9f9' : idx % 2 === 0 ? 'white' : '#fafafa', opacity: isAbsent || onVacation ? 0.6 : 1 }}>
                          <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600', color: '#1f2937' }}>
                            {s.firstName} {s.lastName}
                          </td>
                          <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280', maxWidth: '160px' }}>
                            <div style={{ fontSize: '0.78rem' }}>{s.schoolName}</div>
                          </td>
                          <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                            {dismissal ? (() => {
                              const [h, m] = dismissal.split(':').map(Number);
                              const ampm = h >= 12 ? 'PM' : 'AM';
                              return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
                            })() : '—'}
                          </td>
                          <td style={{ padding: '0.6rem 0.75rem', color: '#6b7280' }}>
                            {driver?.name || '—'}
                          </td>
                          <td style={{ padding: '0.6rem 0.75rem' }}>
                            {isAbsent ? (
                              <span style={{ fontSize: '0.7rem', backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Absent</span>
                            ) : onVacation ? (
                              <span style={{ fontSize: '0.7rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>Vacation</span>
                            ) : (
                              <span style={{ fontSize: '0.7rem', backgroundColor: statusColor + '18', color: statusColor, padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: '600' }}>{statusLabel}</span>
                            )}
                          </td>
                          <td style={{ padding: '0.6rem 0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {!isAbsent && !onVacation && (
                                <>
                                  <button
                                    onClick={() => handleMarkAbsent(s.id)}
                                    disabled={savingAbsent === s.id}
                                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.68rem', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                  >
                                    Absent Today
                                  </button>
                                  <button
                                    onClick={() => setVacationModal({ studentId: s.id, studentName: `${s.firstName} ${s.lastName}` })}
                                    style={{ padding: '0.2rem 0.5rem', fontSize: '0.68rem', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                  >
                                    Vacation
                                  </button>
                                </>
                              )}
                              {(isAbsent || onVacation) && (
                                <button
                                  onClick={() => handleMarkPresent(s.id)}
                                  disabled={savingAbsent === s.id}
                                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.68rem', backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                  Mark Present
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Stats Overview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <StatsCard value={totalPickupStudents} label="Pickup Students" icon={Users} color="#3b82f6" trend="Active today" />
          <StatsCard value={totalPending} label="Pending Pickup" icon={Clock} color="#f59e0b" trend="Waiting at school" />
          <StatsCard value={totalInTransit} label="In Transit" icon={Car} color="#3b82f6" trend="On the way" />
          <StatsCard value={totalDelivered} label="Delivered" icon={CheckCircle} color="#10b981" trend="At Stemtree" />
        </div>

        {/* System Overview */}
        <div style={styles.card}>
          <div style={styles.flexBetween}>
            <h2 style={{ margin: '0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Bell style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
              System Overview
            </h2>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9375rem' }}>
              ℹ️ All systems operational. {totalPickupStudents} students scheduled for pickup today.
            </p>
          </div>
        </div>
      </div>

      {/* Vacation Modal */}
      {vacationModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '380px' }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '700' }}>Mark on Vacation</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>{vacationModal.studentName}</p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.3rem' }}>Start Date</label>
              <input type="date" value={vacationStart} onChange={e => setVacationStart(e.target.value)} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.3rem' }}>End Date</label>
              <input type="date" value={vacationEnd} onChange={e => setVacationEnd(e.target.value)} style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setVacationModal(null); setVacationStart(''); setVacationEnd(''); }} style={{ flex: 1, padding: '0.7rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSaveVacation} disabled={savingAbsent} style={{ flex: 1, padding: '0.7rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Vacation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;